import { ErrorHandler, Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '../../environments/environment';
import { FrontendVersionSHA } from '../version.constant';

@Injectable({ providedIn: 'root' })
export class SentryIntegrationService implements OnDestroy {
  private module: typeof import('@sentry/angular') | null = null;
  private initPromise?: Promise<void>;
  private traceService?: InstanceType<typeof import('@sentry/angular')['TraceService']>;
  private pendingErrors: unknown[] = [];

  constructor(private readonly router: Router) {}

  init(): Promise<void> {
    if (!environment.production) {
      return Promise.resolve();
    }

    if (this.initPromise) {
      return this.initPromise;
    }

    this.initPromise = this.loadSentry();
    return this.initPromise;
  }

  captureException(error: unknown): void {
    if (!environment.production) {
      return;
    }

    if (this.module) {
      this.module.captureException(error);
      return;
    }

    this.pendingErrors.push(error);
    void this.init();
  }

  ngOnDestroy(): void {
    this.traceService?.ngOnDestroy();
    this.traceService = undefined;
  }

  private async loadSentry(): Promise<void> {
    try {
      const Sentry = await import('@sentry/angular');

      Sentry.init({
        release: FrontendVersionSHA,
        enabled: true,
        dsn: 'https://34df3588eba2bd76944e68fdf4a7f0e3@o4509973761228800.ingest.de.sentry.io/4510295202332752',
        integrations: [
          Sentry.browserTracingIntegration(),
          Sentry.httpClientIntegration(),
          Sentry.replayIntegration({
            maskAllText: true,
            blockAllMedia: true,
          }),
        ],
        tracesSampleRate: 1.0,
        replaysSessionSampleRate: 0.1,
        replaysOnErrorSampleRate: 1.0,
        tracePropagationTargets: ['localhost', /^https:\/\/pt[0-9]+\.gp2\.app/],
      });

      this.module = Sentry;
      if (!this.traceService) {
        this.traceService = new Sentry.TraceService(this.router);
      }

      if (this.pendingErrors.length) {
        for (const pending of this.pendingErrors) {
          Sentry.captureException(pending);
        }
        this.pendingErrors = [];
      }

      if (Sentry.getClient()?.getOptions().enabled) {
        console.log('Sentry is enabled');
      } else {
        console.log('Sentry is disabled');
      }
    } catch (loadError) {
      console.error('Sentry init failed', loadError);
      this.module = null;
      this.initPromise = undefined;
    }
  }
}

@Injectable()
export class SentryErrorHandler extends ErrorHandler {
  constructor(private readonly sentry: SentryIntegrationService) {
    super();
  }

  override handleError(error: unknown): void {
    this.sentry.captureException(error);
    super.handleError(error);
  }
}
